import { createSlice, current } from '@reduxjs/toolkit'
import { remove, unionBy } from 'lodash'

const initialState = {
  success: '',
  hasNext: false,
  page: 0,
  hasPrev: false,
  totalPages: 0,
  totalResult: 0,
  loading: false,
  processing: false,
  campaigns: [],
  nurses: [],
  bookings: [],
  users: [],
  shiftList: [],
  disabledShift: [],
  error: '',
  campaignInfo: {},
  address: {},
  loader: false,

  campaignInfoError: '',
  loadingCampaignInfo: false,
  nurseListError: '',
  loadingNurseList: false,
  shiftListError: '',
  loadingShiftList: false,
  bookedShift: [],
  bookedShiftError: '',
}

const nurseCampaignSlice = createSlice({
  name: 'nurseCampaignSlice',
  initialState: initialState,
  reducers: {
    getCampaigns: () => {},
    getActiveNurses: () => {},
    getBookings: () => {},
    getShifts: () => {},
    getUserInfo: () => {},
    getCampaignInfo: () => {},
    getUserBookedShift: () => {},
    assignCampaignJobToNurse: () => {},
    changeTimeSlotStatus: () => {},
    cancelBookings: () => {},
    bookCampaigns: () => {},
    updateCamapignOrder: () => {},
    addAddress: () => {},
    generateSalesOrder: () => {},
    updateSalesOrder: () => {},
    confirmSalesOrder: () => {},
    setCampaigns: (state, action) => {
      state.campaigns = action.payload
      state.loading = false
    },
    setActiveNurses: (state, action) => {
      const { data } = action.payload
      state.nurses = data
      state.loading = false
    },
    setBookings: (state, action) => {
      const { data, page, total_result, total_pages } = action.payload
      state.bookings = data
      state.page = page
      state.totalResult = total_result
      state.totalPages = total_pages
      state.loading = false
    },
    setTimeSlotStatus: (state, action) => {
      if (action.payload.full) {
        state.disabledShift = [...state.disabledShift, action.payload]
      } else {
        return {
          ...state,
          disabledShift: [...state.disabledShift].filter(
            (shift) => shift.id !== action.payload.id,
          ),
        }
      }
    },
    setUserInfo: (state, action) => {
      state.processing = false
      state.users = action.payload
    },
    setShifts: (state, action) => {
      state.shiftList = action.payload[0].data
      state.disabledShift = action.payload[1].data
    },
    removeAssignedJobs: (state, action) => {
      const { appointments, activeTab, jobs } = action.payload
      if (activeTab.title === 'Assigned') {
        return {
          ...state,
          bookings: unionBy(jobs, current(state.bookings), 'id'),
        }
      } else {
        state.bookings = remove(state.bookings, (bookings) => {
          return !appointments.campaign_appointment_ids.includes(bookings.id)
        })
      }
    },
    removeBooking: (state, action) => {
      return {
        ...state,
        bookings: state.bookings.filter((job) => job.id !== action.payload),
      }
    },
    setCampaignInfo: (state, action) => {
      state.campaignInfo = action.payload
    },
    setAddress: (state, action) => {
      state.address = action.payload
    },
    setCampaignLoading: (state, action) => {
      state.loadingCampaignInfo = action.payload
    },
    setNewBooking: (state, action) => {
      state.bookings = [action.payload, ...state.bookings]
    },
    setLoading: (state) => {
      state.loading = true
      state.error = ''
    },
    setUpdatedOrder: (state, action) => {
      state.bookings = state.bookings.map((booking) => {
        if (booking.id === action.payload.id) {
          return action.payload
        } else {
          return booking
        }
      })
    },
    setSalesOrderStatus: (state, action) => {
      state.bookings = state.bookings.map((booking) => {
        if (booking.id === action.payload.id) {
          return {
            ...booking,
            sales_order: {
              ...action.payload.data,
            },
          }
        } else {
          return booking
        }
      })
    },
    updateSalesOrderStatus: (state, action) => {
      const { index, data } = action.payload
      state.bookings = state.bookings.map((booking, bookingIndex) => {
        if (index === bookingIndex) {
          return {
            ...booking,
            sales_order: {
              ...booking.sales_order,
              ...data,
            },
          }
        } else {
          return booking
        }
      })
    },
    setUserBookedShift: (state, action) => {
      state.bookedShift = action.payload
    },
    setProcessing: (state, action) => {
      state.processing = action.payload ?? true
      state.error = ''
    },
    setLoader: (state, action) => {
      state.loader = action.payload ?? true
      state.error = ''
      state.bookedShiftError = ''
    },
    setError: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    setCampaignInfoError: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    setErrorByType: (state, action) => {
      const { type, error } = action.payload
      if (type === 'CampaignInfo') {
        state.loadingCampaignInfo = false
        state.campaignInfoError = error
      } else if (type === 'NurseList') {
        state.loadingNurseList = false
        state.nurseListError = error
      } else {
        state.loadingShiftList = false
        state.shiftListError = error
      }
    },
    setLoaderByType: (state, action) => {
      const { type, loader } = action.payload
      if (type === 'CampaignInfo') {
        state.loadingCampaignInfo = loader ?? false
      } else if (type === 'NurseList') {
        state.loadingNurseList = loader ?? false
      } else {
        state.loadingShiftList = loader ?? false
      }
    },
    setUserBookedShiftError: (state, action) => {
      state.bookedShiftError = action.payload.error;
    }
  },
})

export const {
  getCampaigns,
  getActiveNurses,
  getBookings,
  getShifts,
  getUserInfo,
  getCampaignInfo,
  generateSalesOrder,
  getUserBookedShift,
  updateSalesOrderStatus,
  updateSalesOrder,
  confirmSalesOrder,
  assignCampaignJobToNurse,
  changeTimeSlotStatus,
  bookCampaigns,
  addAddress,
  updateCamapignOrder,
  setError,
  setLoading,
  setActiveNurses,
  setBookings,
  setUserInfo,
  setCampaigns,
  setUserBookedShift,
  setProcessing,
  setShifts,
  setLoader,
  setNewBooking,
  setSalesOrderStatus,
  setCampaignInfo,
  setCampaignLoading,
  setTimeSlotStatus,
  setUpdatedOrder,
  setAddress,
  setLoaderByType,
  setErrorByType,
  removeBooking,
  removeAssignedJobs,
  cancelBookings,
  setUserBookedShiftError
} = nurseCampaignSlice.actions

export default nurseCampaignSlice.reducer
